import './DeleteUserConfirmationModal.scss'
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import React, {useContext, useEffect, useState, useMemo} from "react";
import {Modal} from "../../Smart/Modal/Modal";
import {deleteUsersAPI} from "../../../Services/api";
import {GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {DataTable} from "../../DataTable/DataTable";
import {ScrollableListCell} from "../../../Utils/ScrollableListCell/ScrollableListCell";
import {get} from "lodash";

/**
 * Modal component for confirming bulk user deletion.
 * Uses locally available data from the grid instead of fetching from API.
 * 
 * @param {object} params - The component parameters
 * @param {boolean} params.deleteUserConfirmationModalOpen - Whether the modal is open
 * @param {function} params.onCloseDeleteUserConfirmationModal - Function to close the modal
 * @param {function} params.onSuccess - Function to call on successful deletion
 * @returns {JSX.Element} The delete user confirmation modal
 */
export const DeleteUserConfirmationModal = ({
    deleteUserConfirmationModalOpen,
    onCloseDeleteUserConfirmationModal,
    onSuccess
}) => {
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState(null)
    const [deletionResult, setDeletionResult] = useState(null)
    const [isExplanationExpanded, setIsExplanationExpanded] = useState(false)
    const gridContext = useContext(GridReactContext)
    const {i18n} = useContext(I18nReactContext)
    
    // Memoize selectionModel to prevent unnecessary re-renders
    const selectionModel = useMemo(() => gridContext.selectionModel, [gridContext.selectionModel])
    
    // Use selectedRows which contains the FULL data for selected users
    // DataGridWrapper updates this whenever selection changes (line 304-305)
    const usersToDeleteWithData = useMemo(() => {
        // gridContext.selectedRows already contains the full user objects for selected IDs
        return gridContext.selectedRows || [];
    }, [gridContext.selectedRows])
    
    // Reset state when modal closes
    useEffect(() => {
        if (!deleteUserConfirmationModalOpen) {
            setError(null);
            setDeletionResult(null);
            setIsExplanationExpanded(false);
        }
    }, [deleteUserConfirmationModalOpen])

    const onDeleteUsers = async () => {
        try {
            setDeleting(true)
            setError(null)
            
            // Send ALL selected users to backend - backend will handle validation
            const response = await deleteUsersAPI({userIds: selectionModel})
            
            // Store the result to show deleted and skipped users
            setDeletionResult(response);
            
            // If any users were deleted, trigger table refresh
            if (response.deleted > 0 && gridContext.triggerTableRefresh) {
                gridContext.triggerTableRefresh();
            }
            
            // Don't call onSuccess here - let user review results and close manually
        }
        catch (e) {
            setError(i18n.translate('SADeleteUsersModal.deleteError'))
        } finally {
            setDeleting(false)
        }
    }
    
    const handleClose = () => {
        // Call onSuccess after user has reviewed results and closed the modal
        if (deletionResult && deletionResult.deleted > 0 && onSuccess) {
            onSuccess();
        }
        
        setDeletionResult(null);
        setError(null);
        onCloseDeleteUserConfirmationModal();
    }
    
    // Show confirmation view (before deletion) or results view (after deletion)
    const showConfirmation = !deletionResult;
    const showResults = deletionResult !== null;
    
    // Helper function to get translated result message
    const getResultMessage = () => {
        if (!deletionResult) return '';
        
        const {deleted} = deletionResult;
        
        if (deleted > 0) {
            return i18n.translate('SADeleteUsersModal.successMessageOnly', {context: {deleted}});
        } else {
            return i18n.translate('SADeleteUsersModal.noDeletedMessage');
        }
    };
    
    // Memoized base columns for results view (AFTER deletion - filtered columns)
    // Only recreates when i18n changes (language switch)
    const resultsBaseColumns = useMemo(() => [
        { key: 'firstName', label: i18n.translate('SADeleteUsersModal.firstName'), width: 150 },
        { key: 'lastName', label: i18n.translate('SADeleteUsersModal.lastName'), width: 150 },
        { key: 'email', label: i18n.translate('SADeleteUsersModal.email'), width: 300 },
        { key: 'employeeId', label: i18n.translate('SADeleteUsersModal.employeeID'), width: 150 },
        { key: 'idNumber', label: i18n.translate('SADeleteUsersModal.idNumber'), width: 150 }
    ], [i18n]);

    // Memoized columns for confirmation view (BEFORE deletion - show all data including organizations)
    const confirmationColumns = useMemo(() => [
        ...resultsBaseColumns,
        { 
            key: 'organizations', 
            label: i18n.translate('SADeleteUsersModal.organizations'), 
            width: 300,
            render: (user) => {
                const organizations = get(user, 'row.organizations', []);
                if (organizations.length === 0) {
                    return '-';
                }
                const organizationNames = organizations.map((org) => get(org, 'orgName', '')).join(', ');
                return <ScrollableListCell value={organizationNames} />;
            }
        }
    ], [resultsBaseColumns, i18n]);

    // Memoized columns for skipped users (includes reason column)
    const skippedColumns = useMemo(() => [
        ...resultsBaseColumns,
        {
            key: 'reason',
            label: i18n.translate('SADeleteUsersModal.skipReason'),
            width: 350,
            render: (user) => {
                const row = user.row;
                if (row.reason === 'cannot_delete_organizational_admin') {
                    const orgs = row.adminOfOrganizations?.join(', ') || '';
                    return (
                        <div className="skip-reason-cell">
                            <div className="reason-title">{i18n.translate('SADeleteUsersModal.reasons.cannot_delete_organizational_admin')}</div>
                            {orgs && (
                                <div className="reason-details">
                                    {i18n.translate('SADeleteUsersModal.reasons.adminOf')}: {orgs}
                                </div>
                            )}
                        </div>
                    );
                } else {
                    // Translate reason keys from backend (e.g., 'cannot_delete_self', 'cannot_delete_superadmin', etc.)
                    const translationKey = `SADeleteUsersModal.reasons.${row.reason}`;
                    return <div className="reason-title">{i18n.translate(translationKey)}</div>;
                }
            }
        }
    ], [resultsBaseColumns, i18n]);

    return <Modal
        open={deleteUserConfirmationModalOpen}
        onClose={showResults ? handleClose : onCloseDeleteUserConfirmationModal}
        data-testid="delete-confirmation-modal">
        <div className="SADeleteUsersModal modalWrapper">
            <div className="modalTitle">
                <div>{showConfirmation ? i18n.translate('SADeleteUsersModal.title') : i18n.translate('SADeleteUsersModal.resultsTitle')}</div>
            </div>
            <div className="modalContent">
                {/* Error State */}
                {error && (
                    <div data-testid="error-message" className="error-message">
                        {error}
                    </div>
                )}

                {/* CONFIRMATION VIEW - Before Deletion */}
                {showConfirmation && !error && (
                    <>
                        <div className="message">
                            {i18n.translate('SADeleteUsersModal.confirmMessage', {context: {userCount: selectionModel.length}})}
                        </div>
                        <div className="hint-message">
                            {i18n.translate('SADeleteUsersModal.confirmHint')}
                        </div>
                        <DataTable
                            columns={confirmationColumns}
                            data={usersToDeleteWithData}
                            getRowKey={(row) => row.id}
                            className="usersToDelete"
                            pageSize={10}
                            showPagination={true}
                        />
                    </>
                )}

                {/* RESULTS VIEW - After Deletion */}
                {showResults && !error && (
                    <>
                        {/* Success Summary */}
                        {deletionResult.deleted > 0 && (
                            <div className="success-message">
                                <div className="success-icon">✅</div>
                                <div className="success-text">{getResultMessage()}</div>
                            </div>
                        )}

                        {/* No Deletions Warning */}
                        {deletionResult.deleted === 0 && (
                            <div className="warning-message">
                                <div className="warning-icon">⚠️</div>
                                <div className="warning-text">{getResultMessage()}</div>
                            </div>
                        )}

                        {/* Deleted Users Table */}
                        {deletionResult.deletedUsers && deletionResult.deletedUsers.length > 0 && (
                            <div className="deleted-users-section">
                                <div className="section-title success">
                                    ✅ {i18n.translate('SADeleteUsersModal.deletedUsersTitle')} ({deletionResult.deletedUsers.length})
                                </div>
                                <DataTable
                                    columns={resultsBaseColumns}
                                    data={deletionResult.deletedUsers}
                                    getRowKey={(row) => row.userId}
                                    className="deletedUsersTable"
                                    pageSize={5}
                                    showPagination={true}
                                />
                            </div>
                        )}

                        {/* Skipped Users Table */}
                        {deletionResult.skippedUsers && deletionResult.skippedUsers.length > 0 && (
                            <div className="skipped-users-section">
                                <div className="section-title error">
                                    ⚠️ {i18n.translate('SADeleteUsersModal.skippedUsersTitle')} ({deletionResult.skippedUsers.length})
                                </div>
                                
                                <div className="skipped-explanation">
                                    <div 
                                        className="explanation-header" 
                                        onClick={() => setIsExplanationExpanded(!isExplanationExpanded)}
                                    >
                                        <div className="explanation-title">
                                            {i18n.translate('SADeleteUsersModal.skippedExplanationTitle')}
                                        </div>
                                        <div className="expand-icon">
                                            {isExplanationExpanded ? '▼' : '◀'}
                                        </div>
                                    </div>
                                    {isExplanationExpanded && (
                                        <div className="explanation-text">
                                            {i18n.translate('SADeleteUsersModal.skippedExplanation')}
                                        </div>
                                    )}
                                </div>

                                <DataTable
                                    columns={skippedColumns}
                                    data={deletionResult.skippedUsers}
                                    getRowKey={(row) => row.userId}
                                    className="skippedUsersTable"
                                    pageSize={5}
                                    showPagination={true}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="modalFooter">
                {showConfirmation && (
                    <>
                        <Button
                            data-testid="cancel-delete-button"
                            color={buttonColors.YELLOW}
                            onClick={onCloseDeleteUserConfirmationModal}
                            disabled={deleting}
                            text={i18n.translate('general.close')}
                        />
                        <Button
                            data-testid="confirm-delete-button"
                            disabled={(!usersToDeleteWithData || usersToDeleteWithData.length === 0) || deleting}
                            color={buttonColors.GREY}
                            onClick={onDeleteUsers}
                            text={deleting ? i18n.translate('SADeleteUsersModal.deleting') : i18n.translate('SADeleteUsersModal.deleteUsers')}
                        />
                    </>
                )}
                {showResults && (
                    <Button
                        data-testid="close-results-button"
                        color={buttonColors.YELLOW}
                        onClick={handleClose}
                        text={i18n.translate('general.close')}
                    />
                )}
            </div>
        </div>
    </Modal>
}